// ============================================================================
// OpenInAppCTA — shown ONLY on iPhone / iPad Safari (where we removed the
// Google sign-in button because iOS Safari's ITP breaks the OAuth callback).
// ----------------------------------------------------------------------------
// Steers iPhone users toward the native App Store build (where Apple Sign-In
// is the first-class OAuth flow) without shaming them or breaking the layout.
//
// Rendered on /sign-in and /signup. Auto-hidden on desktop, Android, and
// the App Store app itself.
// ============================================================================

import { useState, useEffect } from "react";
import { ExternalLink, Smartphone } from "lucide-react";
import { isNativeIOS } from "../lib/platform";

// Update this once the app is approved and you have the concrete App Store
// URL from App Store Connect (looks like:
// https://apps.apple.com/us/app/bilateral-calm/id1234567890 ).
// Until then, the shortname form auto-redirects once Apple indexes it.
export const APP_STORE_URL = "https://apps.apple.com/app/bilateral-calm";

const DISMISS_KEY = "bc_open_in_app_dismissed_v1";

const isIOSMobileWebSurface = () => {
  if (typeof window === "undefined" || isNativeIOS()) return false;
  const ua = window.navigator?.userAgent || "";
  const isIPadOS13Plus =
    /Macintosh/i.test(ua) &&
    typeof document !== "undefined" &&
    "ontouchend" in document;
  return /iPhone|iPad|iPod/i.test(ua) || isIPadOS13Plus;
};

export const OpenInAppCTA = () => {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isIOSMobileWebSurface()) return;
    try {
      if (window.sessionStorage.getItem(DISMISS_KEY) === "1") return;
    } catch { /* private-mode / storage quota — treat as not dismissed */ }
    setVisible(true);
  }, []);

  const dismiss = () => {
    try { window.sessionStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="mb-6 rounded-2xl border border-[#3090A8]/20 bg-[#3090A8]/5 p-5"
      data-testid="open-in-app-cta"
    >
      <div className="flex gap-3 items-start">
        <Smartphone className="w-5 h-5 text-[#3090A8] shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <h3 className="font-outfit font-semibold text-[#1B2349] text-[15px] mb-1">
            Prefer Google sign-in? Get the iOS app.
          </h3>
          <p className="text-sm text-[#4A5568] leading-relaxed mb-3">
            Mobile Safari can drop OAuth mid-flight, so we've kept sign-in
            here to Apple and email. The native app has the smoothest flow.
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <a
              href={APP_STORE_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#1B2349] hover:bg-[#0F1633] text-white text-sm font-semibold px-4 py-2 transition-colors"
              data-testid="open-in-app-cta-store-link"
            >
              Get it on the App Store
              <ExternalLink className="w-3.5 h-3.5" />
            </a>
            <button
              type="button"
              onClick={dismiss}
              className="text-sm text-[#718096] hover:text-[#4A5568] underline underline-offset-2"
              data-testid="open-in-app-cta-dismiss"
            >
              Continue here
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpenInAppCTA;
